import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, ChangeEvent } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkDirective from 'remark-directive'
import { remarkCallouts } from '../../lib/remark-callouts'

// MarkdownEditor — spec 19 §2 shared primitive. Used by spec 19 Briefings
// and spec 22 Notes (compact mode).
//
// Source of truth: the textarea. The toolbar mutates the string via
// selection-aware insert/wrap; no contenteditable, no DOM tree. Preview
// is render-only via react-markdown with a small allowlist + the
// :::warn callout extension.

type ToolbarItem =
  | 'bold'
  | 'italic'
  | 'h2'
  | 'h3'
  | 'ul'
  | 'ol'
  | 'link'
  | 'callout-warn'
  | '|'

const DEFAULT_TOOLBAR: ToolbarItem[] = [
  'bold', 'italic', '|',
  'h2', 'h3', '|',
  'ul', 'ol', '|',
  'link', 'callout-warn'
]

// Compact mode (spec 22 Notes) hides H2 / H3 / Callout — Notes are
// short timeline entries, not authored documents.
const COMPACT_TOOLBAR: ToolbarItem[] = [
  'bold', 'italic', '|',
  'ul', 'ol', '|',
  'link'
]

const ALLOWED_TAGS = new Set([
  'h2', 'h3', 'h4',
  'p', 'ul', 'ol', 'li',
  'strong', 'em', 'code',
  'a',
  'div' // for our :::warn callouts
])

type MarkdownEditorProps = {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  minHeight?: number
  maxLength?: number
  preview?: 'split' | 'tab' | 'off'
  compact?: boolean
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder = '',
  minHeight = 200,
  maxLength = 10000,
  preview = 'tab',
  compact = false
}: MarkdownEditorProps) {
  const taRef = useRef<HTMLTextAreaElement | null>(null)
  const [tab, setTab] = useState<'write' | 'preview'>('write')

  const toolbar = compact ? COMPACT_TOOLBAR : DEFAULT_TOOLBAR

  // Replace the textarea's current selection with `next`, preserving
  // selection range over the inserted text. `next` is the new substring
  // that should replace [selStart, selEnd]; `selectAfterStart` and
  // `selectAfterEnd` are offsets into `next` defining where to leave the
  // caret/selection.
  function replaceSelection(
    next: string,
    selectAfterStart = next.length,
    selectAfterEnd = next.length
  ) {
    const ta = taRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const before = value.slice(0, start)
    const after = value.slice(end)
    const newValue = before + next + after
    if (newValue.length > maxLength) return
    onChange(newValue)
    // Restore selection after React's re-render. The setState→render→
    // effect chain is async, so schedule on next microtask.
    requestAnimationFrame(() => {
      const t = taRef.current
      if (!t) return
      t.focus()
      const base = start
      t.setSelectionRange(base + selectAfterStart, base + selectAfterEnd)
    })
  }

  // Wrap the selected text in `prefix`/`suffix`. If nothing's selected,
  // insert prefix + placeholder + suffix and select the placeholder.
  function wrap(prefix: string, suffix: string, placeholderText = 'text') {
    const ta = taRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = value.slice(start, end)
    if (selected) {
      replaceSelection(
        prefix + selected + suffix,
        prefix.length,
        prefix.length + selected.length
      )
    } else {
      replaceSelection(
        prefix + placeholderText + suffix,
        prefix.length,
        prefix.length + placeholderText.length
      )
    }
  }

  // Insert at the start of the current line (or each line of the selection).
  // Used for headings and lists.
  function prefixLines(prefix: string | ((i: number) => string)) {
    const ta = taRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    // Expand selection out to line bounds.
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    const block = value.slice(lineStart, end)
    const lines = block.split('\n')
    const prefixed = lines
      .map((line, i) => (typeof prefix === 'function' ? prefix(i) : prefix) + line)
      .join('\n')
    const before = value.slice(0, lineStart)
    const after = value.slice(end)
    const next = before + prefixed + after
    if (next.length > maxLength) return
    onChange(next)
    requestAnimationFrame(() => {
      const t = taRef.current
      if (!t) return
      t.focus()
      t.setSelectionRange(lineStart, lineStart + prefixed.length)
    })
  }

  function insertCallout() {
    const ta = taRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = value.slice(start, end) || 'Warning text'
    // Pad with newlines so the directive parses cleanly.
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    const needsLeadingBreak = lineStart !== start
    const lead = needsLeadingBreak ? '\n\n' : ''
    const inserted = `${lead}:::warn\n${selected}\n:::\n`
    replaceSelection(
      inserted,
      lead.length + ':::warn\n'.length,
      lead.length + ':::warn\n'.length + selected.length
    )
  }

  function insertLink() {
    const ta = taRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = value.slice(start, end) || 'link text'
    replaceSelection(
      `[${selected}](https://)`,
      1,
      1 + selected.length
    )
  }

  function applyToolbar(item: ToolbarItem) {
    switch (item) {
      case 'bold':
        wrap('**', '**')
        break
      case 'italic':
        wrap('*', '*')
        break
      case 'h2':
        prefixLines('## ')
        break
      case 'h3':
        prefixLines('### ')
        break
      case 'ul':
        prefixLines('- ')
        break
      case 'ol':
        prefixLines((i) => `${i + 1}. `)
        break
      case 'link':
        insertLink()
        break
      case 'callout-warn':
        insertCallout()
        break
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    const mod = e.metaKey || e.ctrlKey
    if (mod && !e.shiftKey && !e.altKey) {
      const k = e.key.toLowerCase()
      if (k === 'b') {
        e.preventDefault()
        wrap('**', '**')
        return
      }
      if (k === 'i') {
        e.preventDefault()
        wrap('*', '*')
        return
      }
      if (k === 'k') {
        e.preventDefault()
        insertLink()
        return
      }
    }
    // Tab inserts 2 spaces; Shift+Tab outdents.
    if (e.key === 'Tab') {
      e.preventDefault()
      const ta = taRef.current
      if (!ta) return
      const start = ta.selectionStart
      const end = ta.selectionEnd
      if (e.shiftKey) {
        // Outdent: remove up to 2 leading spaces from each affected line.
        const lineStart = value.lastIndexOf('\n', start - 1) + 1
        const block = value.slice(lineStart, end)
        const lines = block.split('\n')
        const out = lines
          .map((l) => (l.startsWith('  ') ? l.slice(2) : l.startsWith(' ') ? l.slice(1) : l))
          .join('\n')
        const next = value.slice(0, lineStart) + out + value.slice(end)
        onChange(next)
        return
      }
      // Indent: just insert 2 spaces at cursor (no selection-aware indent
      // for v2.0 — multi-line indent rarely needed for these short bodies).
      replaceSelection('  ')
    }
  }

  function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
    const next = e.target.value
    if (next.length > maxLength) return
    onChange(next)
  }

  // Soft length warning at > 80%.
  const length = value.length
  const warnLength = length > maxLength * 0.8

  // Auto-grow up to 3x minHeight.
  useEffect(() => {
    const ta = taRef.current
    if (!ta) return
    ta.style.height = 'auto'
    const max = minHeight * 3
    ta.style.height = Math.min(Math.max(ta.scrollHeight, minHeight), max) + 'px'
    ta.style.overflowY = ta.scrollHeight > max ? 'auto' : 'hidden'
  }, [value, minHeight])

  return (
    <div className="border border-line rounded bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-1 border-b border-line px-2 py-1.5 flex-wrap">
        {toolbar.map((item, idx) =>
          item === '|' ? (
            <span key={idx} className="w-px h-5 bg-line mx-1" aria-hidden />
          ) : (
            <ToolbarButton key={idx} item={item} onClick={() => applyToolbar(item)} />
          )
        )}
        {preview === 'tab' && (
          <div className="ml-auto flex items-center gap-1 text-xs">
            <TabButton active={tab === 'write'} onClick={() => setTab('write')}>
              Write
            </TabButton>
            <TabButton active={tab === 'preview'} onClick={() => setTab('preview')}>
              Preview
            </TabButton>
          </div>
        )}
      </div>

      {/* Body */}
      {preview === 'split' ? (
        <div className="grid grid-cols-2 divide-x divide-line">
          <textarea
            ref={taRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{ minHeight }}
            className="w-full p-3 text-sm font-mono resize-none focus:outline-none"
          />
          <div className="p-3 prose prose-sm max-w-none">
            <MarkdownPreview value={value} />
          </div>
        </div>
      ) : tab === 'write' || preview === 'off' ? (
        <textarea
          ref={taRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{ minHeight }}
          className="w-full p-3 text-sm font-mono resize-none focus:outline-none"
        />
      ) : (
        <div className="p-3 prose prose-sm max-w-none" style={{ minHeight }}>
          <MarkdownPreview value={value} />
        </div>
      )}

      {/* Length counter — only past 80% */}
      {warnLength && (
        <div className="border-t border-line px-3 py-1.5 text-xs text-ink-soft font-mono text-right">
          {length.toLocaleString('en-GB')} / {maxLength.toLocaleString('en-GB')}
        </div>
      )}
    </div>
  )
}

function ToolbarButton({
  item,
  onClick
}: {
  item: Exclude<ToolbarItem, '|'>
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={LABELS[item]}
      aria-label={LABELS[item]}
      className="px-2 py-1 text-xs rounded hover:bg-parchment text-ink"
    >
      {GLYPHS[item]}
    </button>
  )
}

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded ${
        active ? 'bg-parchment text-ink font-semibold' : 'text-ink-soft hover:bg-parchment'
      }`}
    >
      {children}
    </button>
  )
}

const LABELS: Record<Exclude<ToolbarItem, '|'>, string> = {
  bold: 'Bold (Cmd/Ctrl+B)',
  italic: 'Italic (Cmd/Ctrl+I)',
  h2: 'Heading 2',
  h3: 'Heading 3',
  ul: 'Bulleted list',
  ol: 'Numbered list',
  link: 'Link (Cmd/Ctrl+K)',
  'callout-warn': 'Warning callout'
}

const GLYPHS: Record<Exclude<ToolbarItem, '|'>, string> = {
  bold: 'B',
  italic: 'I',
  h2: 'H2',
  h3: 'H3',
  ul: '•',
  ol: '1.',
  link: '🔗',
  'callout-warn': '⚠'
}

// Preview — react-markdown with allowlist + remark-directive + the
// :::warn callout extension. Anything outside the allowlist falls through
// to plain text.
function MarkdownPreview({ value }: { value: string }) {
  if (!value.trim()) {
    return <p className="text-ink-soft italic">Nothing to preview yet.</p>
  }
  return (
    <ReactMarkdown
      remarkPlugins={[remarkDirective, remarkCallouts]}
      allowedElements={Array.from(ALLOWED_TAGS)}
      components={{
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        )
      }}
    >
      {value}
    </ReactMarkdown>
  )
}
